import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, FiArrowLeft, FiArrowRight, FiCheck, FiTruck, FiAward, 
  FiActivity, FiBox, FiCircle, FiFileText, FiMapPin, FiDollarSign, 
  FiSettings, FiCpu, FiShield, FiCalendar, FiLock, FiUnlock,
  FiPlus, FiMinus, FiEdit, FiTrash2, FiCopy
} from 'react-icons/fi';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useKYC } from '../../contexts/KYCContext';
import { useBlockchain } from '../../contexts/BlockchainContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import toast from 'react-hot-toast';

const VEHICLE_CATEGORIES = {
  SUPERCAR: {
    id: 'supercar',
    name: 'Supercar',
    description: 'High-performance luxury sports cars',
    baseValue: '200000+',
    icon: '🏎️',
    color: '#ff6b6b'
  },
  HYPERCAR: {
    id: 'hypercar',
    name: 'Hypercar',
    description: 'Ultra-exclusive, limited-production vehicles',
    baseValue: '1000000+',
    icon: '⚡',
    color: '#4ecdc4'
  },
  GRAND_TOURER: {
    id: 'grand_tourer',
    name: 'Grand Tourer',
    description: 'Luxury long-distance performance cars',
    baseValue: '150000+',
    icon: '🏁',
    color: '#45b7d1'
  },
  TRACK_CAR: {
    id: 'track_car',
    name: 'Track Car',
    description: 'Circuit-focused racing vehicles',
    baseValue: '300000+',
    icon: '🏆',
    color: '#f9ca24'
  }
};

const PART_CATEGORIES = {
  ENGINE: {
    id: 'engine',
    name: 'Engine & Powertrain',
    description: 'Engine, transmission, hybrid systems',
    composable: true,
    critical: true,
    icon: FiCpu,
    subParts: [
      { id: 'engine_block', name: 'Engine Block', critical: true, tradeable: false },
      { id: 'transmission', name: 'Transmission', critical: true, tradeable: true },
      { id: 'hybrid_system', name: 'Hybrid System', critical: false, tradeable: true },
      { id: 'exhaust_system', name: 'Exhaust System', critical: false, tradeable: true },
      { id: 'turbocharger', name: 'Turbocharger', critical: false, tradeable: true },
      { id: 'cooling_system', name: 'Cooling System', critical: true, tradeable: true }
    ]
  },
  AERODYNAMICS: {
    id: 'aerodynamics',
    name: 'Aerodynamics',
    description: 'Wings, splitters, diffusers',
    composable: true,
    critical: false,
    icon: FiActivity,
    subParts: [
      { id: 'front_wing', name: 'Front Wing', critical: false, tradeable: true },
      { id: 'rear_wing', name: 'Rear Wing', critical: false, tradeable: true },
      { id: 'splitter', name: 'Front Splitter', critical: false, tradeable: true },
      { id: 'diffuser', name: 'Rear Diffuser', critical: false, tradeable: true },
      { id: 'side_skirts', name: 'Side Skirts', critical: false, tradeable: true },
      { id: 'air_ducts', name: 'Air Ducts', critical: false, tradeable: true }
    ]
  },
  WHEELS: {
    id: 'wheels',
    name: 'Wheels & Tires',
    description: 'Wheels, tires, braking systems',
    composable: true,
    critical: true,
    icon: FiCircle,
    subParts: [
      { id: 'front_wheels', name: 'Front Wheels', critical: true, tradeable: true },
      { id: 'rear_wheels', name: 'Rear Wheels', critical: true, tradeable: true },
      { id: 'front_tires', name: 'Front Tires', critical: true, tradeable: true },
      { id: 'rear_tires', name: 'Rear Tires', critical: true, tradeable: true },
      { id: 'brake_calipers', name: 'Brake Calipers', critical: true, tradeable: true },
      { id: 'brake_discs', name: 'Brake Discs', critical: true, tradeable: true }
    ]
  },
  INTERIOR: {
    id: 'interior',
    name: 'Interior Components',
    description: 'Seats, dashboard, electronics',
    composable: true,
    critical: false,
    icon: FiBox,
    subParts: [
      { id: 'driver_seat', name: 'Driver Seat', critical: false, tradeable: true },
      { id: 'passenger_seat', name: 'Passenger Seat', critical: false, tradeable: true },
      { id: 'dashboard', name: 'Dashboard', critical: false, tradeable: false },
      { id: 'steering_wheel', name: 'Steering Wheel', critical: false, tradeable: true },
      { id: 'center_console', name: 'Center Console', critical: false, tradeable: true },
      { id: 'door_panels', name: 'Door Panels', critical: false, tradeable: true }
    ]
  },
  ELECTRONICS: {
    id: 'electronics',
    name: 'Electronics & Systems',
    description: 'ECUs, sensors, infotainment',
    composable: true,
    critical: true,
    icon: FiCpu,
    subParts: [
      { id: 'main_ecu', name: 'Main ECU', critical: true, tradeable: false },
      { id: 'engine_ecu', name: 'Engine ECU', critical: true, tradeable: false },
      { id: 'abs_system', name: 'ABS System', critical: true, tradeable: true },
      { id: 'infotainment', name: 'Infotainment System', critical: false, tradeable: true },
      { id: 'telemetry', name: 'Telemetry System', critical: false, tradeable: true },
      { id: 'sensors_package', name: 'Sensors Package', critical: false, tradeable: true }
    ]
  },
  BODYWORK: {
    id: 'bodywork',
    name: 'Bodywork & Panels',
    description: 'Carbon fiber panels, doors, hoods',
    composable: true,
    critical: false,
    icon: FiShield,
    subParts: [
      { id: 'front_hood', name: 'Front Hood', critical: false, tradeable: true },
      { id: 'roof_panel', name: 'Roof Panel', critical: false, tradeable: true },
      { id: 'front_doors', name: 'Front Doors', critical: false, tradeable: true },
      { id: 'rear_panels', name: 'Rear Panels', critical: false, tradeable: true },
      { id: 'side_panels', name: 'Side Panels', critical: false, tradeable: true },
      { id: 'bumpers', name: 'Bumpers', critical: false, tradeable: true }
    ]
  }
};

const WizardContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;

  .brand-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;

    .mclaren-logo {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #4facfe 0%, #3b82f6 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      color: #fff;
      font-weight: bold;
    }

    .partner-badge {
      background: rgba(79, 172, 254, 0.1);
      border: 1px solid rgba(79, 172, 254, 0.3);
      border-radius: 20px;
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
      color: #4facfe;
      font-weight: 600;
    }
  }

  h1 {
    color: #fff;
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }

  .subtitle {
    color: #a0a0a0;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .description {
    color: #666;
    font-size: 0.95rem;
    max-width: 600px;
    margin: 0 auto;
  }

  .close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a0a0a0;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  gap: 1rem;

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;

    .step-circle {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      transition: all 0.3s ease;

      &.pending {
        background: rgba(255, 255, 255, 0.1);
        color: #a0a0a0;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }

      &.active {
        background: linear-gradient(135deg, #4facfe 0%, #3b82f6 100%);
        color: #fff;
        border: 2px solid #4facfe;
        box-shadow: 0 0 20px rgba(79, 172, 254, 0.3);
      }

      &.completed {
        background: linear-gradient(135deg, #00ff88 0%, #00d4aa 100%);
        color: #fff;
        border: 2px solid #00ff88;
      }
    }

    .step-label {
      font-size: 0.875rem;
      color: #a0a0a0;
      text-align: center;
    }
  }

  .step-connector {
    width: 60px;
    height: 2px;
    background: rgba(255, 255, 255, 0.2);
    margin-top: -20px;

    &.completed {
      background: linear-gradient(90deg, #00ff88 0%, #4facfe 100%);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    
    .step-connector {
      width: 2px;
      height: 30px;
      margin-top: 0;
      margin-left: -20px;
    }
  }
`;

const StepContent = styled(motion.div)`
  padding: 2rem;
  flex: 1;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .icon {
      color: #4facfe;
    }
  }

  .section-description {
    color: #a0a0a0;
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr 1fr'};
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    color: #fff;
    font-weight: 500;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  input, select, textarea {
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
    font-size: 0.95rem;
    transition: all 0.3s ease;

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    &:focus {
      outline: none;
      border-color: #4facfe;
      background: rgba(79, 172, 254, 0.1);
      box-shadow: 0 0 0 2px rgba(79, 172, 254, 0.2);
    }

    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const CategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    background: rgba(79, 172, 254, 0.1);
    border-color: #4facfe;
    transform: translateY(-2px);
  }

  &.selected {
    background: rgba(79, 172, 254, 0.2);
    border-color: #4facfe;
    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
  }

  .category-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .category-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .category-description {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .category-value {
    color: #4facfe;
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const PartCategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

// New styled components for part details
const PartDetailsSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .part-header {
    display: flex;
    justify-content: between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .part-info {
      .part-name {
        font-size: 1.1rem;
        font-weight: 600;
        color: #4facfe;
        margin-bottom: 0.25rem;
      }

      .part-category {
        font-size: 0.9rem;
        color: #a0a0a0;
      }
    }

    .part-actions {
      display: flex;
      gap: 0.5rem;

      button {
        background: rgba(255, 255, 255, 0.1);
        border: none;
        border-radius: 6px;
        padding: 0.5rem;
        color: #a0a0a0;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(79, 172, 254, 0.2);
          color: #4facfe;
        }

        &.danger:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }
      }
    }
  }
`;

const PartSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  .part-option {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      background: rgba(79, 172, 254, 0.1);
      border-color: #4facfe;
    }

    &.selected {
      background: rgba(79, 172, 254, 0.2);
      border-color: #4facfe;
    }

    .part-name {
      font-weight: 600;
      color: #fff;
      margin-bottom: 0.5rem;
    }

    .part-badges {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;

      .badge {
        padding: 0.25rem 0.5rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 500;

        &.critical {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        &.tradeable {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        &.non-tradeable {
          background: rgba(156, 163, 175, 0.2);
          color: #9ca3af;
        }
      }
    }

    .selected-indicator {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      width: 24px;
      height: 24px;
      background: rgba(0, 255, 136, 0.2);
      border: 1px solid #00ff88;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;



const PartCategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(79, 172, 254, 0.1);
    border-color: #4facfe;
  }

  &.selected {
    background: rgba(79, 172, 254, 0.15);
    border-color: #4facfe;
  }

  .part-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;

    .part-icon {
      color: #4facfe;
      font-size: 1.25rem;
    }

    .part-info {
      flex: 1;

      .part-name {
        font-weight: 600;
        color: #fff;
        font-size: 1rem;
      }

      .part-description {
        color: #a0a0a0;
        font-size: 0.85rem;
      }
    }
  }

  .part-badges {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;

    .badge {
      padding: 0.25rem 0.5rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 500;

      &.critical {
        background: rgba(239, 68, 68, 0.2);
        color: #ef4444;
      }

      &.composable {
        background: rgba(79, 172, 254, 0.2);
        color: #4facfe;
      }
    }
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .action-group {
    display: flex;
    gap: 1rem;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #3b82f6 100%);
    color: #fff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const ReviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;

  .card-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .review-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }

    .label {
      color: #a0a0a0;
      font-weight: 500;
      flex: 1;
    }

    .value {
      color: #fff;
      font-weight: 600;
      text-align: right;
      flex: 1;
      
      &.highlight {
        color: #4facfe;
      }
    }
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(79, 172, 254, 0.1);
      border-color: #4facfe;
    }

    input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #4facfe;
    }

    .item-content {
      flex: 1;

      .item-title {
        color: #fff;
        font-weight: 500;
        margin-bottom: 0.25rem;
      }

      .item-description {
        color: #a0a0a0;
        font-size: 0.85rem;
      }
    }
  }
`;

const steps = [
  { id: 1, label: 'Vehicle Type' },
  { id: 2, label: 'Details & Specs' },
  { id: 3, label: 'Composability' },
  { id: 4, label: 'Part Details' },
  { id: 5, label: 'Documentation' },
  { id: 6, label: 'Review & Deploy' }
];

function VehicleTokenizationWizard({ onClose }) {
  const { createAsset } = useTokenization();
  const { createAssetClass } = useAssetClass();
  const { kycData } = useKYC();
  const { mintTokens } = useBlockchain();
  const { currentIssuer, hasPermission } = useTokenIssuer();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Vehicle Information
    vehicleCategory: '',
    vehicleName: '',
    manufacturer: 'McLaren',
    model: '',
    year: '',
    vin: '',
    engineNumber: '',
    
    // Specifications
    engineType: '',
    horsepower: '',
    torque: '',
    topSpeed: '',
    acceleration: '',
    transmission: '',
    fuelType: '',
    drivetrain: '',
    
    // Valuation & Ownership
    estimatedValue: '',
    currency: 'USD',
    currentOwner: '',
    condition: 'Excellent',
    mileage: '',
    
    // Composability
    enableComposability: false,
    selectedPartCategories: [],
    allowPartTrading: false,
    custodianAddress: '',
    restrictedParts: [],
    
    // Individual Part Details
    partDetails: {},
    
    // Documentation
    documents: [],
    certifications: [],
    maintenanceRecords: [],
    
    // Blockchain Settings
    tokenStandard: 'CBC721',
    royaltyPercentage: 2.5,
    
    // Additional
    description: '',
    location: '',
    insuranceValue: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const togglePartCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      selectedPartCategories: prev.selectedPartCategories.includes(categoryId)
        ? prev.selectedPartCategories.filter(id => id !== categoryId)
        : [...prev.selectedPartCategories, categoryId]
    }));
  };

  const addPartDetail = (categoryId, partId) => {
    const category = PART_CATEGORIES[categoryId];
    const subPart = category?.subParts?.find(p => p.id === partId);
    
    if (!subPart) return;

    const partKey = `${categoryId}_${partId}`;
    setFormData(prev => ({
      ...prev,
      partDetails: {
        ...prev.partDetails,
        [partKey]: {
          categoryId,
          partId,
          name: subPart.name,
          critical: subPart.critical,
          tradeable: subPart.tradeable,
          // Default values for part details
          manufacturer: 'McLaren',
          partNumber: '',
          serialNumber: '',
          condition: 'New',
          installationDate: '',
          warrantyExpiry: '',
          lastServiceDate: '',
          nextServiceDue: '',
          estimatedValue: '',
          purchasePrice: '',
          insuranceValue: '',
          currency: 'USD',
          description: '',
          maintenanceHistory: [],
          certifications: []
        }
      }
    }));
  };

  const removePartDetail = (partKey) => {
    setFormData(prev => ({
      ...prev,
      partDetails: {
        ...prev.partDetails,
        [partKey]: undefined
      }
    }));
  };

  const updatePartDetail = (partKey, field, value) => {
    setFormData(prev => {
      // Auto-create part detail if it doesn't exist
      if (!prev.partDetails[partKey]) {
        const [categoryId, partId] = partKey.split('_');
        const category = PART_CATEGORIES[categoryId];
        const subPart = category?.subParts?.find(p => p.id === partId);
        
        if (subPart) {
          return {
            ...prev,
            partDetails: {
              ...prev.partDetails,
              [partKey]: {
                categoryId,
                partId: subPart.id,
                name: subPart.name,
                critical: subPart.critical,
                tradeable: subPart.tradeable,
                manufacturer: 'McLaren',
                partNumber: '',
                serialNumber: '',
                condition: 'New',
                installationDate: '',
                warrantyExpiry: '',
                lastServiceDate: '',
                nextServiceDue: '',
                estimatedValue: '',
                purchasePrice: '',
                insuranceValue: '',
                currency: 'USD',
                description: '',
                maintenanceHistory: [],
                certifications: [],
                [field]: value
              }
            }
          };
        }
      }
      
      // Update existing part detail
      return {
        ...prev,
        partDetails: {
          ...prev.partDetails,
          [partKey]: {
            ...prev.partDetails[partKey],
            [field]: value
          }
        }
      };
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      // Create the main vehicle asset
      const vehicleAsset = await createAsset({
        ...formData,
        category: 'AUTOMOTIVE',
        assetType: 'VEHICLE',
        tokenType: 'NFT'
      });

      // Create individual part assets if composability is enabled
      if (formData.enableComposability && Object.keys(formData.partDetails).length > 0) {
        for (const [partKey, partDetail] of Object.entries(formData.partDetails)) {
          if (partDetail) {
            await createAsset({
              ...partDetail,
              parentVehicleId: vehicleAsset.id,
              category: 'AUTOMOTIVE',
              assetType: 'VEHICLE_PART',
              tokenType: 'NFT',
              vehicleName: formData.vehicleName,
              vehicleVin: formData.vin
            });
          }
        }
      }

      // Mint the tokens
      await mintTokens({
        assetId: vehicleAsset.id,
        tokenType: formData.tokenStandard,
        quantity: 1,
        recipient: formData.currentOwner || kycData.walletAddress
      });

      toast.success('Vehicle NFT deployed successfully!');
      onClose();
    } catch (error) {
      console.error('Error deploying vehicle NFT:', error);
      toast.error('Failed to deploy vehicle NFT');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = (files, category) => {
    const newFiles = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      category: category,
      uploadDate: new Date().toISOString()
    }));
    
    setFormData(prev => ({
      ...prev,
      [category]: [...prev[category], ...newFiles]
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiTruck className="icon" />
                Choose Vehicle Category
              </div>
              <div className="section-description">
                Select the category that best describes your McLaren vehicle for optimal tokenization parameters.
              </div>
            </FormSection>

            <CategoryGrid>
              {Object.values(VEHICLE_CATEGORIES).map((category) => (
                <CategoryCard
                  key={category.id}
                  className={formData.vehicleCategory === category.id ? 'selected' : ''}
                  onClick={() => updateFormData('vehicleCategory', category.id)}
                >
                  <div className="category-icon">{category.icon}</div>
                  <div className="category-name">{category.name}</div>
                  <div className="category-description">{category.description}</div>
                  <div className="category-value">Base Value: ${category.baseValue}</div>
                </CategoryCard>
              ))}
            </CategoryGrid>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiSettings className="icon" />
                Vehicle Details & Specifications
              </div>
              <div className="section-description">
                Provide comprehensive details about your McLaren vehicle for accurate tokenization and valuation.
              </div>
            </FormSection>

            <FormGrid>
              <FormGroup>
                <label>Vehicle Name</label>
                <input
                  type="text"
                  placeholder="e.g., McLaren P1 GTR"
                  value={formData.vehicleName}
                  onChange={(e) => updateFormData('vehicleName', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Model</label>
                <input
                  type="text"
                  placeholder="e.g., P1, 720S, Senna"
                  value={formData.model}
                  onChange={(e) => updateFormData('model', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Year</label>
                <input
                  type="number"
                  placeholder="2024"
                  value={formData.year}
                  onChange={(e) => updateFormData('year', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>VIN (Vehicle Identification Number)</label>
                <input
                  type="text"
                  placeholder="17-character VIN"
                  value={formData.vin}
                  onChange={(e) => updateFormData('vin', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Engine Number</label>
                <input
                  type="text"
                  placeholder="Engine serial number"
                  value={formData.engineNumber}
                  onChange={(e) => updateFormData('engineNumber', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Current Owner</label>
                <input
                  type="text"
                  placeholder="Owner name or wallet address"
                  value={formData.currentOwner}
                  onChange={(e) => updateFormData('currentOwner', e.target.value)}
                />
              </FormGroup>
            </FormGrid>

            <FormSection>
              <div className="section-title">
                <FiActivity className="icon" />
                Performance Specifications
              </div>
            </FormSection>

            <FormGrid>
              <FormGroup>
                <label>Engine Type</label>
                <input
                  type="text"
                  placeholder="e.g., Twin-Turbo V8"
                  value={formData.engineType}
                  onChange={(e) => updateFormData('engineType', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Horsepower</label>
                <input
                  type="number"
                  placeholder="720"
                  value={formData.horsepower}
                  onChange={(e) => updateFormData('horsepower', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Torque (lb-ft)</label>
                <input
                  type="number"
                  placeholder="568"
                  value={formData.torque}
                  onChange={(e) => updateFormData('torque', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Top Speed (mph)</label>
                <input
                  type="number"
                  placeholder="212"
                  value={formData.topSpeed}
                  onChange={(e) => updateFormData('topSpeed', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>0-60 mph (seconds)</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="2.8"
                  value={formData.acceleration}
                  onChange={(e) => updateFormData('acceleration', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) => updateFormData('condition', e.target.value)}
                >
                  <option value="New">New</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Very Good">Very Good</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                </select>
              </FormGroup>
            </FormGrid>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <label>Estimated Market Value</label>
                <input
                  type="number"
                  placeholder="500000"
                  value={formData.estimatedValue}
                  onChange={(e) => updateFormData('estimatedValue', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Mileage</label>
                <input
                  type="number"
                  placeholder="12000"
                  value={formData.mileage}
                  onChange={(e) => updateFormData('mileage', e.target.value)}
                />
              </FormGroup>
            </FormRow>

            <FormRow columns="1fr" style={{ marginTop: '2rem' }}>
              <FormGroup>
                <label>Additional Description</label>
                <textarea
                  placeholder="Describe any unique features, history, or special characteristics..."
                  value={formData.description}
                  onChange={(e) => updateFormData('description', e.target.value)}
                  rows={4}
                />
              </FormGroup>
            </FormRow>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <label>
                  Current Location <FiMapPin style={{ color: '#4facfe' }} />
                </label>
                <input
                  type="text"
                  placeholder="City, Country or Facility"
                  value={formData.location}
                  onChange={(e) => updateFormData('location', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <label>Insurance Value</label>
                <input
                  type="number"
                  placeholder="Insured value"
                  value={formData.insuranceValue}
                  onChange={(e) => updateFormData('insuranceValue', e.target.value)}
                />
              </FormGroup>
            </FormRow>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiBox className="icon" />
                CIP-227 Composability Settings
              </div>
              <div className="section-description">
                Enable composable parts tokenization following the CIP-227 standard for enhanced liquidity and part-level trading.
              </div>
            </FormSection>

            <CheckboxGroup>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.enableComposability}
                  onChange={(e) => updateFormData('enableComposability', e.target.checked)}
                />
                <div className="item-content">
                  <div className="item-title">Enable Composable Parts Tokenization</div>
                  <div className="item-description">
                    Allow individual vehicle components to be tokenized separately according to CIP-227 standard
                  </div>
                </div>
              </div>
            </CheckboxGroup>

            {formData.enableComposability && (
              <>
                <FormSection>
                  <div className="section-title">
                    <FiCircle className="icon" />
                    Select Part Categories for Tokenization
                  </div>
                  <div className="section-description">
                    Choose which categories of parts can be tokenized separately. Each part will get its own unique token ID.
                  </div>
                </FormSection>

                <PartCategoryGrid>
                  {Object.values(PART_CATEGORIES).map((category) => (
                    <PartCategoryCard
                      key={category.id}
                      className={formData.selectedPartCategories.includes(category.id) ? 'selected' : ''}
                      onClick={() => togglePartCategory(category.id)}
                    >
                      <div className="part-header">
                        <category.icon className="part-icon" />
                        <div className="part-info">
                          <div className="part-name">{category.name}</div>
                          <div className="part-description">{category.description}</div>
                        </div>
                      </div>
                      <div className="part-badges">
                        {category.critical && <span className="badge critical">Critical</span>}
                        {category.composable && <span className="badge composable">Composable</span>}
                      </div>
                    </PartCategoryCard>
                  ))}
                </PartCategoryGrid>

                <CheckboxGroup>
                  <div className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.allowPartTrading}
                      onChange={(e) => updateFormData('allowPartTrading', e.target.checked)}
                    />
                    <div className="item-content">
                      <div className="item-title">Allow Part Trading</div>
                      <div className="item-description">
                        Enable secondary market trading for individual parts (excluding critical safety components)
                      </div>
                    </div>
                  </div>
                </CheckboxGroup>

                <FormRow columns="1fr">
                  <FormGroup>
                    <label>Custodian Address (Optional)</label>
                    <input
                      type="text"
                      placeholder="Address responsible for part custody verification"
                      value={formData.custodianAddress}
                      onChange={(e) => updateFormData('custodianAddress', e.target.value)}
                    />
                  </FormGroup>
                </FormRow>
              </>
            )}
          </StepContent>
        );

      case 4:
        const totalSelectedParts = Object.keys(formData.partDetails).filter(key => formData.partDetails[key]).length;
        
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiSettings className="icon" />
                Individual Part Details
                {totalSelectedParts > 0 && (
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    fontSize: '0.9rem', 
                    color: '#00ff88',
                    background: 'rgba(0, 255, 136, 0.1)',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px'
                  }}>
                    {totalSelectedParts} parts configured
                  </span>
                )}
              </div>
              <div className="section-description">
                Configure detailed information for each composable part. Each part will receive its own unique token ID and can be traded independently on the marketplace.
              </div>
            </FormSection>

            {totalSelectedParts > 0 && (
              <FormSection style={{ background: 'rgba(0, 255, 136, 0.02)', borderColor: 'rgba(0, 255, 136, 0.2)' }}>
                <div className="section-title" style={{ color: '#00ff88' }}>
                  <FiCheck className="icon" />
                  Summary: {totalSelectedParts} Individual Parts Ready for Tokenization
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '1rem',
                  marginTop: '1rem'
                }}>
                  {Object.entries(formData.partDetails).filter(([key, detail]) => detail).map(([key, detail]) => (
                    <div key={key} style={{ 
                      background: 'rgba(255, 255, 255, 0.05)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      fontSize: '0.9rem'
                    }}>
                      <div style={{ color: '#fff', fontWeight: '600', marginBottom: '0.25rem' }}>{detail.name}</div>
                      <div style={{ color: '#a0a0a0', fontSize: '0.8rem' }}>
                        {detail.partNumber || 'Part number pending'} • {detail.condition || 'New'}
                        {detail.estimatedValue && ` • $${detail.estimatedValue}`}
                      </div>
                    </div>
                  ))}
                </div>
              </FormSection>
            )}

            {formData.enableComposability && formData.selectedPartCategories.length > 0 ? (
              formData.selectedPartCategories.map(categoryId => {
                const category = PART_CATEGORIES[categoryId];
                if (!category) return null;

                return (
                  <FormSection key={categoryId}>
                    <div className="section-title">
                      <category.icon className="icon" />
                      {category.name} Components Configuration
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.9rem', 
                        color: '#4facfe',
                        background: 'rgba(79, 172, 254, 0.1)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px'
                      }}>
                        {category.subParts?.length || 0} parts available
                      </span>
                    </div>
                    <div className="section-description">
                      Configure individual {category.name.toLowerCase()} components for separate tokenization. Each part will receive its own unique token ID.
                    </div>

                    {/* Render detailed forms for ALL parts in the category */}
                    {category.subParts?.map(subPart => {
                      const partKey = `${categoryId}_${subPart.id}`;
                      
                      // Get existing part detail or create a default one for display
                      const partDetail = formData.partDetails[partKey] || {
                        categoryId,
                        partId: subPart.id,
                        name: subPart.name,
                        critical: subPart.critical,
                        tradeable: subPart.tradeable,
                        manufacturer: 'McLaren',
                        partNumber: '',
                        serialNumber: '',
                        condition: 'New',
                        installationDate: '',
                        warrantyExpiry: '',
                        lastServiceDate: '',
                        nextServiceDue: '',
                        estimatedValue: '',
                        purchasePrice: '',
                        insuranceValue: '',
                        currency: 'USD',
                        description: '',
                        maintenanceHistory: [],
                        certifications: []
                      };

                      return (
                        <PartDetailsSection key={partKey}>
                          <div className="part-header">
                            <div className="part-info">
                              <div className="part-name">{subPart.name}</div>
                              <div className="part-category">{category.name}</div>
                            </div>
                            <div className="part-actions">
                              <button
                                type="button"
                                onClick={() => removePartDetail(partKey)}
                                className="danger"
                                title="Remove Part"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>

                          {/* Basic Information */}
                          <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ 
                              color: '#4facfe', 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              marginBottom: '1rem',
                              borderBottom: '1px solid rgba(79, 172, 254, 0.2)',
                              paddingBottom: '0.5rem'
                            }}>
                              Basic Information
                            </h4>
                            <FormGrid>
                              <FormGroup>
                                <label>Manufacturer</label>
                                <input
                                  type="text"
                                  placeholder="Part manufacturer"
                                  value={partDetail.manufacturer || 'McLaren'}
                                  onChange={(e) => updatePartDetail(partKey, 'manufacturer', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Part Number *</label>
                                <input
                                  type="text"
                                  placeholder="Manufacturer part number"
                                  value={partDetail.partNumber || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'partNumber', e.target.value)}
                                  required
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Serial Number *</label>
                                <input
                                  type="text"
                                  placeholder="Unique serial number"
                                  value={partDetail.serialNumber || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'serialNumber', e.target.value)}
                                  required
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Condition *</label>
                                <select
                                  value={partDetail.condition || 'New'}
                                  onChange={(e) => updatePartDetail(partKey, 'condition', e.target.value)}
                                  required
                                >
                                  <option value="New">New</option>
                                  <option value="Excellent">Excellent</option>
                                  <option value="Very Good">Very Good</option>
                                  <option value="Good">Good</option>
                                  <option value="Fair">Fair</option>
                                  <option value="Needs Repair">Needs Repair</option>
                                </select>
                              </FormGroup>
                            </FormGrid>
                          </div>

                          {/* Financial Information */}
                          <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ 
                              color: '#4facfe', 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              marginBottom: '1rem',
                              borderBottom: '1px solid rgba(79, 172, 254, 0.2)',
                              paddingBottom: '0.5rem'
                            }}>
                              Financial & Valuation
                            </h4>
                            <FormGrid>
                              <FormGroup>
                                <label>Estimated Value (USD) *</label>
                                <input
                                  type="number"
                                  placeholder="Part value"
                                  value={partDetail.estimatedValue || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'estimatedValue', e.target.value)}
                                  required
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Purchase Price (USD)</label>
                                <input
                                  type="number"
                                  placeholder="Original purchase price"
                                  value={partDetail.purchasePrice || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'purchasePrice', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Insurance Value (USD)</label>
                                <input
                                  type="number"
                                  placeholder="Insurance coverage value"
                                  value={partDetail.insuranceValue || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'insuranceValue', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Currency</label>
                                <select
                                  value={partDetail.currency || 'USD'}
                                  onChange={(e) => updatePartDetail(partKey, 'currency', e.target.value)}
                                >
                                  <option value="USD">USD</option>
                                  <option value="EUR">EUR</option>
                                  <option value="GBP">GBP</option>
                                  <option value="JPY">JPY</option>
                                </select>
                              </FormGroup>
                            </FormGrid>
                          </div>

                          {/* Lifecycle Information */}
                          <div style={{ marginBottom: '1.5rem' }}>
                            <h4 style={{ 
                              color: '#4facfe', 
                              fontSize: '1rem', 
                              fontWeight: '600', 
                              marginBottom: '1rem',
                              borderBottom: '1px solid rgba(79, 172, 254, 0.2)',
                              paddingBottom: '0.5rem'
                            }}>
                              Lifecycle & Maintenance
                            </h4>
                            <FormGrid>
                              <FormGroup>
                                <label>Installation Date</label>
                                <input
                                  type="date"
                                  value={partDetail.installationDate || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'installationDate', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Warranty Expiry</label>
                                <input
                                  type="date"
                                  value={partDetail.warrantyExpiry || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'warrantyExpiry', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Last Service Date</label>
                                <input
                                  type="date"
                                  value={partDetail.lastServiceDate || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'lastServiceDate', e.target.value)}
                                />
                              </FormGroup>

                              <FormGroup>
                                <label>Next Service Due</label>
                                <input
                                  type="date"
                                  value={partDetail.nextServiceDue || ''}
                                  onChange={(e) => updatePartDetail(partKey, 'nextServiceDue', e.target.value)}
                                />
                              </FormGroup>
                            </FormGrid>
                          </div>

                          <FormRow columns="1fr">
                            <FormGroup>
                              <label>Part Description</label>
                              <textarea
                                placeholder="Detailed description, specifications, or special characteristics..."
                                value={partDetail.description || ''}
                                onChange={(e) => updatePartDetail(partKey, 'description', e.target.value)}
                                rows={3}
                              />
                            </FormGroup>
                          </FormRow>
                        </PartDetailsSection>
                      );
                    })}
                  </FormSection>
                );
              })
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '3rem', 
                color: '#a0a0a0',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '15px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <FiBox style={{ fontSize: '3rem', marginBottom: '1rem', color: '#4facfe' }} />
                <h3 style={{ color: '#fff', marginBottom: '0.5rem' }}>No Parts Selected</h3>
                <p>Enable composability and select part categories in the previous step to configure individual parts.</p>
              </div>
            )}
          </StepContent>
        );

      case 5:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiFileText className="icon" />
                Documentation & Certifications
              </div>
              <div className="section-description">
                Upload supporting documents, certifications, and maintenance records for blockchain verification.
              </div>
            </FormSection>

            <FormGrid>
              <FormGroup>
                <label>Vehicle Documents</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => updateFormData('documents', Array.from(e.target.files))}
                />
                <small style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Title, registration, purchase agreements, inspection reports
                </small>
              </FormGroup>

              <FormGroup>
                <label>Certifications</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => updateFormData('certifications', Array.from(e.target.files))}
                />
                <small style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Authenticity certificates, McLaren certificates, racing certifications
                </small>
              </FormGroup>

              <FormGroup>
                <label>Maintenance Records</label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => updateFormData('maintenanceRecords', Array.from(e.target.files))}
                />
                <small style={{ color: '#a0a0a0', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  Service history, repair records, modification documentation
                </small>
              </FormGroup>
            </FormGrid>

            <FormSection>
              <div className="section-title">
                <FiShield className="icon" />
                Blockchain Configuration
              </div>
            </FormSection>

            <FormRow columns="1fr 1fr">
              <FormGroup>
                <label>Token Standard</label>
                <select
                  value={formData.tokenStandard}
                  onChange={(e) => updateFormData('tokenStandard', e.target.value)}
                >
                  <option value="CBC721">CBC721 (Standard NFT)</option>
                  <option value="CBC1155">CBC1155 (Multi-Token)</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label>Royalty Percentage</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="10"
                  placeholder="2.5"
                  value={formData.royaltyPercentage}
                  onChange={(e) => updateFormData('royaltyPercentage', e.target.value)}
                />
              </FormGroup>
            </FormRow>
          </StepContent>
        );

      case 6:
        const selectedCategory = VEHICLE_CATEGORIES[formData.vehicleCategory];
        const selectedPartsCount = Object.keys(formData.partDetails).filter(key => formData.partDetails[key]).length;

        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiCheck className="icon" />
                Review & Deploy Vehicle NFT
              </div>
              <div className="section-description">
                Review all details before deploying your vehicle NFT to the Core Blockchain.
              </div>
            </FormSection>

            <ReviewGrid>
              <ReviewCard>
                <div className="card-title">
                  <FiTruck className="icon" />
                  Vehicle Information
                </div>
                <div className="review-item">
                  <span className="label">Name</span>
                  <span className="value highlight">{formData.vehicleName}</span>
                </div>
                <div className="review-item">
                  <span className="label">Category</span>
                  <span className="value">{selectedCategory?.name || 'Not Selected'}</span>
                </div>
                <div className="review-item">
                  <span className="label">Manufacturer</span>
                  <span className="value">{formData.manufacturer}</span>
                </div>
                <div className="review-item">
                  <span className="label">Model</span>
                  <span className="value">{formData.model}</span>
                </div>
                <div className="review-item">
                  <span className="label">Year</span>
                  <span className="value">{formData.year}</span>
                </div>
                <div className="review-item">
                  <span className="label">VIN</span>
                  <span className="value">{formData.vin}</span>
                </div>
              </ReviewCard>

              <ReviewCard>
                <div className="card-title">
                  <FiDollarSign className="icon" />
                  Valuation & Ownership
                </div>
                <div className="review-item">
                  <span className="label">Market Value</span>
                  <span className="value highlight">
                    {formData.estimatedValue && `${parseInt(formData.estimatedValue).toLocaleString()} ${formData.currency}`}
                  </span>
                </div>
                <div className="review-item">
                  <span className="label">Current Owner</span>
                  <span className="value">{formData.currentOwner}</span>
                </div>
                <div className="review-item">
                  <span className="label">Condition</span>
                  <span className="value">{formData.condition}</span>
                </div>
                <div className="review-item">
                  <span className="label">Mileage</span>
                  <span className="value">{formData.mileage ? `${parseInt(formData.mileage).toLocaleString()} miles` : 'N/A'}</span>
                </div>
                {formData.location && (
                  <div className="review-item">
                    <span className="label">Location</span>
                    <span className="value">{formData.location}</span>
                  </div>
                )}
              </ReviewCard>

              <ReviewCard>
                <div className="card-title">
                  <FiSettings className="icon" />
                  Performance Specs
                </div>
                {formData.engineType && (
                  <div className="review-item">
                    <span className="label">Engine</span>
                    <span className="value">{formData.engineType}</span>
                  </div>
                )}
                {formData.horsepower && (
                  <div className="review-item">
                    <span className="label">Horsepower</span>
                    <span className="value">{formData.horsepower} HP</span>
                  </div>
                )}
                {formData.torque && (
                  <div className="review-item">
                    <span className="label">Torque</span>
                    <span className="value">{formData.torque} lb-ft</span>
                  </div>
                )}
                {formData.topSpeed && (
                  <div className="review-item">
                    <span className="label">Top Speed</span>
                    <span className="value">{formData.topSpeed} mph</span>
                  </div>
                )}
                {formData.acceleration && (
                  <div className="review-item">
                    <span className="label">0-60 mph</span>
                    <span className="value">{formData.acceleration}s</span>
                  </div>
                )}
              </ReviewCard>

              <ReviewCard>
                <div className="card-title">
                  <FiBox className="icon" />
                  Composability & Parts
                </div>
                <div className="review-item">
                  <span className="label">Composability</span>
                  <span className="value">{formData.enableComposability ? 'Enabled' : 'Disabled'}</span>
                </div>
                {formData.enableComposability && (
                  <>
                    <div className="review-item">
                      <span className="label">Part Categories</span>
                      <span className="value">{formData.selectedPartCategories.length}</span>
                    </div>
                    <div className="review-item">
                      <span className="label">Individual Parts</span>
                      <span className="value highlight">{selectedPartsCount}</span>
                    </div>
                    <div className="review-item">
                      <span className="label">Part Trading</span>
                      <span className="value">{formData.allowPartTrading ? 'Allowed' : 'Restricted'}</span>
                    </div>
                    {formData.custodianAddress && (
                      <div className="review-item">
                        <span className="label">Custodian</span>
                        <span className="value">{formData.custodianAddress.slice(0, 10)}...</span>
                      </div>
                    )}
                  </>
                )}
              </ReviewCard>

              <ReviewCard>
                <div className="card-title">
                  <FiFileText className="icon" />
                  Documentation
                </div>
                <div className="review-item">
                  <span className="label">Documents</span>
                  <span className="value">{formData.documents.length} files</span>
                </div>
                <div className="review-item">
                  <span className="label">Certifications</span>
                  <span className="value">{formData.certifications.length} files</span>
                </div>
                <div className="review-item">
                  <span className="label">Maintenance</span>
                  <span className="value">{formData.maintenanceRecords.length} files</span>
                </div>
                <div className="review-item">
                  <span className="label">Token Standard</span>
                  <span className="value highlight">{formData.tokenStandard}</span>
                </div>
                <div className="review-item">
                  <span className="label">Royalty</span>
                  <span className="value">{formData.royaltyPercentage}%</span>
                </div>
              </ReviewCard>

              {formData.enableComposability && formData.selectedPartCategories.length > 0 && (
                <ReviewCard>
                  <div className="card-title">
                    <FiBox className="icon" />
                    Individual Parts Summary
                  </div>
                  {formData.selectedPartCategories.map(categoryId => {
                    const category = PART_CATEGORIES[categoryId];
                    if (!category) return null;

                    const categoryParts = Object.entries(formData.partDetails).filter(
                      ([key, part]) => part && part.categoryId === categoryId
                    );

                    return (
                      <div key={categoryId} className="review-item">
                        <span className="label">{category.name}</span>
                        <span className="value">
                          {categoryParts.length} part{categoryParts.length !== 1 ? 's' : ''}
                          {category.critical && ' • Critical'}
                          {formData.allowPartTrading && ' • Tradeable'}
                        </span>
                      </div>
                    );
                  })}
                  <div className="review-item">
                    <span className="label">Total Parts</span>
                    <span className="value highlight">{selectedPartsCount} unique tokens</span>
                  </div>
                </ReviewCard>
              )}
            </ReviewGrid>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <Header>
        <button className="close-btn" onClick={onClose}>
          <FiX />
        </button>
        
        <div className="brand-section">
          <div className="mclaren-logo">M</div>
          <div className="partner-badge">Official Partner</div>
        </div>
        
        <h1>Vehicle Tokenization</h1>
        <div className="subtitle">CIP-227 Composable Vehicle NFTs</div>
        <div className="description">
          Professional tokenization for McLaren and luxury automotive assets with full composability support
        </div>
      </Header>

      <StepIndicator>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="step">
              <div className={`step-circle ${
                currentStep === step.id ? 'active' : 
                currentStep > step.id ? 'completed' : 'pending'
              }`}>
                {currentStep > step.id ? <FiCheck /> : step.id}
              </div>
              <div className="step-label">{step.label}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`step-connector ${currentStep > step.id ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </StepIndicator>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <Actions>
        <div className="action-group">
          {currentStep > 1 && (
            <Button className="secondary" onClick={prevStep}>
              <FiArrowLeft />
              Previous
            </Button>
          )}
        </div>
        
        <div className="action-group">
          {currentStep < steps.length ? (
            <Button className="primary" onClick={nextStep}>
              Next
              <FiArrowRight />
            </Button>
          ) : (
            <Button 
              className="primary" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deploying...' : 'Deploy Vehicle NFT'}
              <FiCheck />
            </Button>
          )}
        </div>
      </Actions>
    </WizardContainer>
  );
}

export default VehicleTokenizationWizard; 